import {
    Box,
    Button,
    Flex,
    FormControl,
    Heading,
    HStack,
    Select,
    Text,
    useToast,
} from "@chakra-ui/react";

import AppInput from "components/AppInput";
import AppSelect from "components/AppSelect";
import { useFormik } from "formik";
import { useApi } from "hooks/useApi";
import { useCache } from "hooks/useCache";
import { useContext, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Store } from "store";
import { showToast, stringifyErrors, toastTypes } from "utils";
import * as Yup from "yup";

export default function ManageTaxNaturePage() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    dispatch,
    state: { user },
  } = useContext(Store);
  const [institution, setInstitution] = useState("");
  const {
    getTaxNature,
    addTaxNature,
    updateTaxNature,
    getTaxFrequencies,
    getInstitutions,
    getSubAreas,
  } = useApi();
  const {
    data: taxNature,
    isLoading,
    mutate,
  } = useCache("taxNature", () => getTaxNature(+id || 0));
  const {
    data: taxFrequencies,
    isLoading: isTaxFrequencyLoading,
    mutate: mutateTaxFrequency,
  } = useCache("taxFrequencies", () => getTaxFrequencies(1, ""));

  const {
    data: institutions,
    isLoading: isInstitutionLoading,
    mutate: mutateInstitution,
  } = useCache("institutions", () => getInstitutions(1, institution, ""));

  const {
    data: subAreas,
    isLoading: isSubAreaLoading,
    mutate: mutateSubArea,
  } = useCache("subAreas", () => getSubAreas(1, "", "", institution));

  const initialValues = useMemo(() => {
    return {
      name: taxNature?.display_name || "",
      institution:
        taxNature?.institution_id || user?.user?.institution_id || "",
      subArea: taxNature?.sub_area_id || [],
      taxFrequency: taxNature?.tax_frequency_id || "",
      chargeType: taxNature?.amount > 0 ? "money" : "rate",
      amount: taxNature?.amount || 0,
      rate: taxNature?.rate || 0,
      submit: null,
    };
  }, [taxNature, user]);

  const validationSchema = Yup.object().shape({
    name: Yup.string().trim().required("Nom requis"),
    institution: Yup.string().trim().required("Institution requis"),
    subArea: Yup.array()
      .of(
        Yup.object({
          value: Yup.string(),
        })
      )
      .min(1, "Zone requis")
      .required("Zone requis"),
    taxFrequency: Yup.string().trim().required("Périodicité requis"),
    chargeType: Yup.string().trim().required("Type de fréquence requis"),
    amount: Yup.string().trim(),
    rate: Yup.string().trim(),
  });

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        // let formData: any = new FormData();
        // formData.append("name", values.name);
        // formData.append("sub_area_id", values.subArea);
        // formData.append("institution_id", values.institution);
        // formData.append("tax_frequency_id", values.taxFrequency);
        // formData.append("amount", values.amount);
        // formData.append("rate", values.rate);
        // formData.append("is_active", true);
        const postData = {
          name: values.name,
          sub_area_id: values.subArea,
          institution_id: values.institution,
          tax_frequency_id: values.taxFrequency,
          amount: values.amount,
          rate: values.rate,
          is_active: true,
        };
        if (id) {
          postData["id"] = id;
        }
        let response: any = null;
        if (id) {
          response = await updateTaxNature(postData);
        } else {
          response = await addTaxNature(postData);
        }
        if (!response.error_code && !response.errors) {
          if (response.id) {
            dispatch(
              showToast({
                toast: toast,
                message: id
                  ? "Nature de la Créance mis à jour avec succès"
                  : "Nature de la Créance ajouté avec succès",
                status: toastTypes.success,
              })
            );
            if (id) {
              mutate(getTaxNature(+id));
              navigate("/tax-natures");
            } else {
              navigate("/tax-natures");
            }
          }
        } else {
          dispatch(
            showToast({
              toast: toast,
              message: response.errors
                ? stringifyErrors(response.errors)
                : response.message,
              status: toastTypes.error,
            })
          );
        }
      } catch (ex) {
        console.log(ex);
        dispatch(
          showToast({
            toast: toast,
            message: "Quelque chose s'est mal passé",
            status: toastTypes.error,
          })
        );
      }
    },
  });
  return (
    <>
      {!isLoading && (
        <Flex
          gap="18px"
          bg="very_light_gray"
          flex={1}
          flexDirection="column"
          py="20px"
          px="30px"
          height={"100vh"}
        >
          <Box>
            <Flex gap="4px" flexDirection="column">
              <Flex justifyContent="center" alignItems="center">
                <Heading size="text2xl" as="h4" fontFamily="Arimo">
                  {id
                    ? "Modifier la nature de règlement"
                    : "Créer une nature de règlement"}
                </Heading>
                <Flex
                  pl="56px"
                  pr="62px"
                  gap="9px"
                  flex={1}
                  justifyContent="flex-end"
                  px={{ md: 0, base: "20px" }}
                ></Flex>
              </Flex>
            </Flex>
          </Box>
          <form
            onSubmit={handleSubmit}
            style={{
              flexDirection: "column",
              margin: "0px",
              alignItems: "center",
            }}
          >
            <Flex flexDirection="column" alignItems="center">
              <Flex
                gap="16px"
                bg="white"
                boxShadow="xl"
                w="100%"
                flexDirection="column"
                px={{ base: "20px", sm: "24px" }}
                py={{ base: "20px", sm: "24px" }}
                mx={{ md: "62px", base: "0px" }}
                borderRadius="16px"
              >
                <Flex gap="16px" flexDirection="column">
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Nom</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={!!errors.name && (touched.name as boolean)}
                      >
                        <AppInput
                          required
                          placeholder={`Nom`}
                          borderWidth="1px"
                          alignSelf="stretch"
                          borderRadius="8px"
                          id="name"
                          name="name"
                          autoComplete="name"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          value={values.name}
                          showErrorMessage={Boolean(
                            touched.name && errors.name
                          )}
                          errorMessage={errors.name}
                        />
                      </FormControl>
                    </Flex>
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Périodicité</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.taxFrequency &&
                          (touched.taxFrequency as boolean)
                        }
                      ></FormControl>
                      {!isTaxFrequencyLoading && (
                        <AppSelect
                          id="taxFrequency"
                          name="taxFrequency"
                          onChange={(field: string, value: any) => {
                            setFieldValue(field, value);
                            mutateTaxFrequency(getTaxFrequencies(1, ""));
                          }}
                          value={
                            values.taxFrequency || initialValues.taxFrequency
                          }
                          placeholder="Veuillez sélectionner une périodicité"
                          options={taxFrequencies}
                          isMulti={false}
                          isLoading={isTaxFrequencyLoading}
                          showErrorMessage={Boolean(
                            touched.taxFrequency && errors.taxFrequency
                          )}
                          errorMessage={errors.taxFrequency}
                          onBlur={handleBlur}
                        />
                      )}
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                      mt={1}
                    >
                      <HStack>
                        <Text color="gray_description">Institution</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.institution &&
                          (touched.institution as boolean)
                        }
                      >
                        {!isInstitutionLoading &&
                          !user?.user?.institution_id && (
                            <AppSelect
                              id="institution"
                              name="institution"
                              onChange={(field: string, value: any) => {
                                setFieldValue(field, value);
                                setInstitution(value);
                                mutateInstitution(
                                  getInstitutions(1, value, "")
                                );
                                mutateSubArea(getSubAreas(1, "", "", value));
                              }}
                              value={
                                values.institution || initialValues.institution
                              }
                              placeholder="Veuillez sélectionner une institution"
                              options={institutions}
                              isMulti={false}
                              isLoading={isInstitutionLoading}
                              showErrorMessage={Boolean(
                                touched.institution && errors.institution
                              )}
                              errorMessage={errors.institution}
                              onBlur={handleBlur}
                            />
                          )}
                      </FormControl>
                    </Flex>
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Zone</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.subArea && (touched.subArea as boolean)
                        }
                      ></FormControl>
                      {!isSubAreaLoading && (
                        <AppSelect
                          id="subArea"
                          isMulti={true}
                          name="subArea"
                          onChange={(field: string, value: any) => {
                            setFieldValue(field, value);
                          }}
                          value={values.subArea || initialValues.subArea}
                          placeholder="Veuillez sélectionner une zone"
                          options={subAreas}
                          isLoading={isSubAreaLoading}
                          showErrorMessage={Boolean(
                            touched.subArea && errors.subArea
                          )}
                          errorMessage={errors.subArea}
                          onBlur={handleBlur}
                        />
                      )}
                    </Flex>
                  </Flex>
                  <Flex
                    flexDirection={{ md: "row", base: "column" }}
                    w={"full"}
                    gap={5}
                  >
                    <Flex
                      gap="4px"
                      flexDirection="column"
                      alignItems="start"
                      w={"full"}
                    >
                      <HStack>
                        <Text color="gray_description">Type de paiement</Text>
                        <span style={{ color: "red" }}>*</span>
                      </HStack>
                      <FormControl
                        isInvalid={
                          !!errors.chargeType && (touched.chargeType as boolean)
                        }
                      ></FormControl>
                      <>
                        <Select
                          id="chargeType"
                          name="chargeType"
                          onChange={handleChange}
                          value={values.chargeType || initialValues.chargeType}
                          onBlur={handleBlur}
                        >
                          <option value="">
                            Veuillez sélectionner un type de paiement
                          </option>
                          <option value="money">Montant de la créance</option>
                          <option value="rate">Taux autorisé</option>
                        </Select>
                      </>
                      {errors.chargeType && (
                        <span style={{ color: "red" }}>
                          {errors.chargeType}
                        </span>
                      )}
                    </Flex>
                    {values.chargeType === "money" && (
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        w={"full"}
                        mt={1}
                      >
                        <HStack>
                          <Text color="gray_description">
                            Montant de la créance
                          </Text>
                        </HStack>
                        <FormControl
                          isInvalid={
                            !!errors.amount && (touched.amount as boolean)
                          }
                        >
                          <AppInput
                            type="number"
                            placeholder={`Montant de la créance`}
                            borderWidth="1px"
                            alignSelf="stretch"
                            borderRadius="8px"
                            id="amount"
                            name="amount"
                            autoComplete="amount"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.amount}
                            showErrorMessage={Boolean(
                              touched.amount && errors.amount
                            )}
                            errorMessage={errors.amount}
                          />
                        </FormControl>
                      </Flex>
                    )}
                    {values.chargeType === "rate" && (
                      <Flex
                        gap="4px"
                        flexDirection="column"
                        alignItems="start"
                        w={"full"}
                        mt={1}
                      >
                        <HStack>
                          <Text color="gray_description">Taux autorisé</Text>
                        </HStack>
                        <FormControl
                          isInvalid={!!errors.rate && (touched.rate as boolean)}
                        >
                          <AppInput
                            type="number"
                            placeholder={`Taux autorisé`}
                            borderWidth="1px"
                            alignSelf="stretch"
                            borderRadius="8px"
                            id="rate"
                            name="rate"
                            autoComplete="rate"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            value={values.rate}
                            showErrorMessage={Boolean(
                              touched.rate && errors.rate
                            )}
                            errorMessage={errors.rate}
                          />
                        </FormControl>
                      </Flex>
                    )}
                  </Flex>
                </Flex>
              </Flex>
              <Flex mt="24px" gap="12px">
                <Button
                  size="4xl"
                  color="gray_description"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="106px"
                  borderRadius="8px"
                  onClick={() => window.history.back()}
                >
                  Annuler
                </Button>
                <Button
                  size="4xl"
                  colorScheme="red_origin"
                  fontFamily="Arimo"
                  fontWeight={700}
                  minW="132px"
                  borderRadius="8px"
                  type="submit"
                  isLoading={isSubmitting}
                >
                  Enregistrer
                </Button>
              </Flex>
            </Flex>
          </form>
        </Flex>
      )}
    </>
  );
}
